import React, { useMemo } from 'react';

import { Typography } from '@material-ui/core';
import List, { ListProps } from '@material-ui/core/List';
import { PcpMode } from 'core/SelectPCP';
import _ from 'lodash';
import moment from 'moment';
import { TAvailability, TFacility, TProvider } from 'ui-model';

import { EntityID } from '@lib/resources/types';

import { useCurrentTimezone } from 'hooks';

import CardFacilityAvailabilities from './CardFacilityAvailabilities';

const groupAvailabilities = (itemsAgregated: TAvailability[], timezone: string, primaryCareProvider?: EntityID) =>
  // Group availabilities by date
  _.filter(
    _.mapValues(
      _.groupBy(itemsAgregated, (item) => moment(item.date).format('MM/DD/YYYY')),
      (availabilitiesGroupedByDate, date) => ({
        date: moment(date),
        // Group availabilities by facility
        group: _.filter(
          _.mapValues(
            _.groupBy(availabilitiesGroupedByDate, 'facility._id'),
            (availabilitiesGroupedByFacility, facilityId) => ({
              facility: _.get(
                _.find(itemsAgregated, (item) => item.facility._id === facilityId),
                'facility',
              ) as TFacility,
              // Group availabilities by provider
              group: _.filter(
                _.mapValues(
                  _.groupBy(availabilitiesGroupedByFacility, 'provider._id'),
                  (availabilities, providerId) => ({
                    provider: _.get(
                      _.find(itemsAgregated, (item) => item.provider._id === providerId),
                      'provider',
                    ) as TProvider,
                    availabilities,
                  }),
                ),
                (value) => !primaryCareProvider || value.provider._id === primaryCareProvider,
              ),
            }),
          ),
          (value) => !_.isEmpty(value.group),
        ),
      }),
    ),
    (value) => !_.isEmpty(value.group),
  );

export interface ListAvailabilitiesGroupedProps extends Omit<ListProps, 'classes' | 'onChange'> {
  availabilities: TAvailability[];
  isMobile?: boolean;
  pcpMode?: PcpMode;
  primaryCareProvider?: EntityID;
  facilities?: TFacility[];
  onChange: (availability: TAvailability) => void;
}

const ListAvailabilitiesGrouped: React.FC<ListAvailabilitiesGroupedProps> = ({
  availabilities,
  isMobile,
  pcpMode,
  primaryCareProvider,
  facilities,
  onChange,
  ...rest
}) => {
  const timezone = useCurrentTimezone();

  const data = useMemo(
    () => groupAvailabilities(availabilities, timezone, pcpMode === 'pcp' ? primaryCareProvider : undefined),
    [availabilities, timezone, pcpMode, primaryCareProvider],
  );

  return (
    <>
      {_.map(data, (groupByDate, dateKey) => (
        <div key={dateKey} style={{ display: 'inline-block', width: '100%' }}>
          <Typography variant="body1" gutterBottom>
            <b>
              {moment(groupByDate.date)
                .utc()
                .format('dddd, MMM DD')}
            </b>
          </Typography>
          <List {...rest}>
            {_.map(groupByDate.group, (groupByFacility, idx) => (
              <CardFacilityAvailabilities
                key={`${idx}-${groupByFacility.facility._id}`}
                isMobile={!!isMobile}
                groupByFacility={groupByFacility}
                primaryCareProvider={primaryCareProvider}
                onAvailabilityChange={onChange}
                facilities={facilities}
              />
            ))}
          </List>
        </div>
      ))}
    </>
  );
};

export default ListAvailabilitiesGrouped;
